import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { resolveUrl } from "@/lib/content";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ImagePickerDialog } from "./ImagePickerDialog";

const EMPTY = { name: "", description: "", url: "", image_url: "", price_range: "", category: "general", order: 0 };

export const WishlistItemDialog = ({ open, item, onClose, onSaved }) => {
  const { token } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (open) setForm({ ...EMPTY, ...(item || {}) });
  }, [open, item]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const headers = { Authorization: `Bearer ${token}` };
    const payload = { ...form, description: form.description || null, image_url: form.image_url || null, price_range: form.price_range || null };
    try {
      if (item?.id) await axios.put(`${API}/admin/wishlist/${item.id}`, payload, { headers });
      else await axios.post(`${API}/admin/wishlist`, payload, { headers });
      toast.success(item?.id ? "Gift updated" : "Gift added");
      onSaved();
      onClose();
    } catch (error) {
      toast.error("Failed to save gift");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="wishlist-editor-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-charcoal">{item?.id ? "Edit Gift" : "Add Gift"}</DialogTitle>
          <DialogDescription className="font-body text-stone-grey">Saves instantly to the live site.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="font-body text-sm text-stone-grey mb-1 block">Name</Label>
            <Input data-testid="wishlist-editor-name" value={form.name} onChange={set("name")} required className="form-input" />
          </div>
          <div>
            <Label className="font-body text-sm text-stone-grey mb-1 block">Link</Label>
            <Input data-testid="wishlist-editor-url" type="url" value={form.url} onChange={set("url")} required placeholder="https://" className="form-input" />
          </div>
          <div>
            <Label className="font-body text-sm text-stone-grey mb-1 block">Price range (optional)</Label>
            <Input data-testid="wishlist-editor-price" value={form.price_range || ""} onChange={set("price_range")} placeholder="$100–$250" className="form-input" />
          </div>
          <div>
            <Label className="font-body text-sm text-stone-grey mb-1 block">Description (optional)</Label>
            <Textarea data-testid="wishlist-editor-description" value={form.description || ""} onChange={set("description")} rows={2} className="form-input" />
          </div>
          <div>
            <Label className="font-body text-sm text-stone-grey mb-1 block">Photo (optional)</Label>
            <div className="flex items-center gap-3">
              {form.image_url ? <img src={resolveUrl(form.image_url)} alt="" className="w-16 h-16 rounded object-cover bg-sand/30" /> : <div className="w-16 h-16 rounded bg-sand/30" />}
              <Button type="button" variant="outline" size="sm" onClick={() => setPickerOpen(true)} data-testid="wishlist-editor-pick-image">Choose photo</Button>
              {form.image_url && <Button type="button" variant="ghost" size="sm" onClick={() => setForm({ ...form, image_url: "" })}>Remove</Button>}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" className="btn-primary" disabled={saving} data-testid="wishlist-editor-save">{item?.id ? "Save" : "Add Gift"}</Button>
            <Button type="button" variant="outline" onClick={onClose} data-testid="wishlist-editor-cancel">Cancel</Button>
          </div>
        </form>
        <ImagePickerDialog open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={({ url }) => setForm((f) => ({ ...f, image_url: url }))} title="Choose a gift photo" />
      </DialogContent>
    </Dialog>
  );
};
